import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { List } from '@refinedev/antd'
import {
  Table,
  Tag,
  Avatar,
  Space,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
} from 'antd'
import {
  UserOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

const { Text } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

interface DoctorAppointment {
  id: string
  patientId: string
  patientName: string
  patientEmail: string
  patientPhone: string | null
  patientImage: string | null
  type: string
  typeLabel: string
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  patientNotes: string | null
  doctorNotes: string | null
  createdAt: string
}

const statusColors: Record<string, string> = {
  SCHEDULED: 'default',
  CONFIRMED: 'processing',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
  NO_SHOW: 'volcano',
}

export default function MyConsultationsListPage() {
  const [appointments, setAppointments] = useState<DoctorAppointment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/consultations/my`, {
          headers: getAuthHeaders(),
        })
        if (!res.ok) throw new Error('Failed to fetch')
        const data = await res.json()
        setAppointments(data)
      } catch (err) {
        console.error('Error fetching appointments:', err)
        setError('Greska pri ucitavanju konsultacija')
      } finally {
        setIsLoading(false)
      }
    }
    fetchAppointments()
  }, [])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('sr-Latn-RS', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('sr-Latn-RS', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isUpcoming = (dateStr: string, status: string) => {
    return new Date(dateStr) >= new Date() && status !== 'CANCELLED' && status !== 'COMPLETED'
  }

  const canJoinCall = (dateStr: string, status: string) => {
    const appointmentDate = new Date(dateStr)
    const now = new Date()
    const diffMs = appointmentDate.getTime() - now.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    // Can join 10 minutes before until 60 minutes after for doctors
    return diffMins <= 10 && diffMins >= -60 && status !== 'COMPLETED' && status !== 'CANCELLED'
  }

  const upcomingAppointments = appointments.filter(
    (apt) => isUpcoming(apt.scheduledAt, apt.status)
  )
  const todayAppointments = upcomingAppointments.filter((apt) => {
    const date = new Date(apt.scheduledAt)
    const today = new Date()
    return date.toDateString() === today.toDateString()
  })

  const columns: ColumnsType<DoctorAppointment> = [
    {
      title: 'Pacijent',
      key: 'patient',
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.patientImage}
            icon={!record.patientImage && <UserOutlined />}
            style={{ backgroundColor: '#1e3a5f' }}
          />
          <div>
            <Link to={`/patients/${record.patientId}`}>
              <Text strong style={{ display: 'block' }}>{record.patientName}</Text>
            </Link>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.patientEmail}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'typeLabel',
      key: 'type',
    },
    {
      title: 'Datum i vreme',
      key: 'scheduledAt',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <CalendarOutlined />
            <Text>{formatDate(record.scheduledAt)}</Text>
          </Space>
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTime(record.scheduledAt)} ({record.duration} min)</Text>
          </Space>
        </Space>
      ),
      sorter: (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={statusColors[record.status]}>{record.statusLabel}</Tag>
      ),
    },
    {
      title: 'Akcije',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.patientPhone && (
            <Button
              type="text"
              icon={<PhoneOutlined />}
              href={`tel:${record.patientPhone}`}
              title="Pozovi pacijenta"
            />
          )}
          {canJoinCall(record.scheduledAt, record.status) && (
            // Do NOT gate on record.roomUrl: rooms are JIT-created by the
            // server when the doctor opens the call page (roomUrl is null for
            // seeded/legacy bookings until first join). Gate only on the join
            // window + status, mirroring the detail page's primary CTA.
            <Link to={`/my-consultations/${record.id}/call`}>
              <Button type="primary" icon={<VideoCameraOutlined />}>
                Pridruzi se
              </Button>
            </Link>
          )}
          <Link to={`/my-consultations/${record.id}`}>
            <Button>Detalji</Button>
          </Link>
        </Space>
      ),
    },
  ]

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Ucitavanje..." />
      </div>
    )
  }

  if (error) {
    return (
      <Empty
        description={error}
        style={{ padding: '60px 0' }}
      />
    )
  }

  return (
    <List title="Moje konsultacije">
      {/* Stats Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Danas"
              value={todayAppointments.length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#1e3a5f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Predstojecih"
              value={upcomingAppointments.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#0d9488' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Ukupno"
              value={appointments.length}
              prefix={<VideoCameraOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Today's appointments highlight */}
      {todayAppointments.length > 0 && (
        <Card
          title="Danasnje konsultacije"
          style={{ marginBottom: 24, borderLeft: '4px solid #f97316' }}
          extra={
            <Tag color="orange">{todayAppointments.length} danas</Tag>
          }
        >
          <Table
            dataSource={todayAppointments}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* All appointments */}
      <Card title="Sve konsultacije">
        <Table
          dataSource={appointments}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Ukupno ${total} konsultacija`,
          }}
        />
      </Card>
    </List>
  )
}
